import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneTilt } from "@phosphor-icons/react";
import { API_BASE } from "@/lib/constants";

export default function Feedback() {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Feedback · Stable-ish";
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) {
      toast.error("Please write a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/feedback`, {
        message: message.trim(),
        email: email.trim() || null,
        name: name.trim() || null,
      });
      setSent(true);
    } catch {
      toast.error("Couldn't send. Try again or email hello@stableish.me directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-[#050505] text-white noise"
      data-testid="feedback-page"
    >
      <div className="max-w-2xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 hover:text-[#FF2A2A] mb-10"
          data-testid="feedback-back"
        >
          <ArrowLeft size={12} weight="bold" />
          Back to Stable-ish
        </Link>

        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-4">
          // feedback
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.02]">
          Tell us what&rsquo;s
          <span className="italic font-light text-white/70"> missing.</span>
        </h1>
        <p className="mt-5 text-white/60 leading-relaxed">
          Bug reports, dosing errors, feature ideas, &ldquo;this saved my ass last night&rdquo; — a real
          person reads every message.
        </p>

        {sent ? (
          <div
            className="mt-10 border border-[#FF2A2A]/40 bg-[#FF2A2A]/[0.05] p-6"
            data-testid="feedback-sent"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF2A2A] mb-2">
              // sent
            </div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight">
              Got it. Thank you.
            </h2>
            <p className="mt-3 text-sm text-white/60">
              We&rsquo;ll get back to you if you left an email. Otherwise assume we read it.
            </p>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setMessage("");
                setEmail("");
                setName("");
              }}
              className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 hover:text-[#FF2A2A]"
            >
              Send another →
            </button>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="mt-10 space-y-5"
            data-testid="feedback-form"
          >
            <label className="block">
              <span className="block font-mono text-[9px] uppercase tracking-[0.22em] text-white/45 mb-2">
                your message
              </span>
              <textarea
                required
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What&rsquo;s on your mind?"
                data-testid="feedback-message"
                className="w-full bg-black border border-white/15 focus:border-[#FF2A2A] px-4 py-3 text-white placeholder-white/30 focus:outline-none resize-none"
              />
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block font-mono text-[9px] uppercase tracking-[0.22em] text-white/45 mb-2">
                  your name (optional)
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="feedback-name"
                  className="w-full bg-black border border-white/15 focus:border-[#FF2A2A] px-4 py-3 text-white placeholder-white/30 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="block font-mono text-[9px] uppercase tracking-[0.22em] text-white/45 mb-2">
                  email (optional, for reply)
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="feedback-email"
                  className="w-full bg-black border border-white/15 focus:border-[#FF2A2A] px-4 py-3 text-white placeholder-white/30 focus:outline-none"
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="feedback-submit"
              className="inline-flex items-center gap-3 bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] transition-colors"
            >
              <PaperPlaneTilt size={14} weight="bold" />
              {loading ? "Sending…" : "Send feedback"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
